/**
 * <SmartTable /> — the single, unified table component used everywhere.
 *
 * Built on top of @mui/x-data-grid and the field-type system in this folder.
 * Replaces the bespoke renderers in `TableResultBlock` and the workspace
 * `DataTableCard`.
 *
 * Key features:
 *   • Column-typed cell rendering (number, currency, percent, date, datetime,
 *     boolean, categorical chip, multi-select chips, id, email, url, json,
 *     long_text, null).
 *   • Field-type icons in the column header.
 *   • Row-click drawer with full-record view + prev/next.
 *   • Compact / Standard / Comfortable density (persisted in localStorage).
 *   • Server-side download (CSV / Excel) + Pin-to-dashboard in the toolbar (optional).
 *   • Server-side pagination via `onFetchPage`.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridColumnHeaderParams,
} from '@mui/x-data-grid';
import {
  TableChartOutlined,
  CloudDownloadRounded,
  ArrowDropDownRounded,
  AddchartRounded,
  ViewCompactRounded,
  DensityMediumRounded,
  DensitySmallRounded,
  DensityLargeRounded,
} from '@mui/icons-material';
import type { ColumnHints, Density } from './types';
import { useColumnMeta } from './useColumnMeta';
import { SmartCell } from './SmartCell';
import { FieldTypeIcon } from './icons';
import { useDensity, rowHeightFor } from './density';
import { RowDetailDrawer } from './RowDetailDrawer';
import { formatForSort } from './format';

export interface SmartTableProps {
  // ── Data
  columns: string[];
  rows: any[][];
  /** Total rows in the underlying frame (for pagination footer). */
  rowCount?: number;
  /** True when `rows` is a preview slice and more data exists server-side. */
  truncated?: boolean;
  hints?: ColumnHints;

  /**
   * Per-column minimum pixel widths, by column name.
   *
   * `text` and `long_text` columns flex, and MUI ignores `width` entirely
   * when `flex` is set -- a flexing column gets `minWidth` plus a share of
   * whatever space is left over. On a wide grid there is often nothing left
   * over, so the column that most needs room collapses to the 80px floor
   * while fixed-width columns keep theirs. Raising the floor for a specific
   * column is the only way to stop that.
   */
  columnMinWidths?: Record<string, number>;

  // ── Layout / styling
  fillContainer?: boolean;
  variant?: 'card' | 'embed';
  initialDensity?: Density;
  cardSx?: Record<string, any>;

  // ── Toolbar
  title?: string;
  subtitle?: string;
  showToolbar?: boolean;
  toolbarLeft?: React.ReactNode;
  toolbarActions?: React.ReactNode;

  // ── Pagination
  initialPageSize?: number;
  pageSizeOptions?: number[];
  /** When provided, pages > preview range are fetched on demand. */
  onFetchPage?: (page: number, pageSize: number) => Promise<any[][]>;

  // ── Built-in actions
  onAddToDashboard?: () => void;
  /**
   * Multi-format download. When provided, the toolbar shows a download menu
   * (CSV / Excel) that streams the full dataset from the server.
   */
  onDownload?: (format: 'csv' | 'xlsx') => void | Promise<void>;
  downloadDisabled?: boolean;

  // ── Row interaction
  enableRowDetail?: boolean;
  onRowClick?: (row: any[], idx: number) => void;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];

// ─────────────────────────────────────────────────────────────────────────────
// Internal: column header with field-type icon
// ─────────────────────────────────────────────────────────────────────────────

function HeaderCell({
  params,
  type,
}: {
  params: GridColumnHeaderParams;
  type: import('./types').FieldType;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.625,
        minWidth: 0,
        flex: 1,
      }}
    >
      <FieldTypeIcon
        type={type}
        sx={{
          fontSize: 13,
          color: theme.tokens.text.tertiary,
          flexShrink: 0,
        }}
      />
      <Box
        component="span"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {params.colDef.headerName || params.field}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Density toggle
// ─────────────────────────────────────────────────────────────────────────────

function DensityToggle({
  value,
  onChange,
}: {
  value: Density;
  onChange: (d: Density) => void;
}) {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const Icon =
    value === 'comfortable' ? DensityLargeRounded :
    value === 'standard' ? DensityMediumRounded :
    DensitySmallRounded;
  return (
    <>
      <Tooltip title="Row density" arrow placement="top">
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            width: 28,
            height: 28,
            color: theme.tokens.text.tertiary,
            '&:hover': {
              color: theme.tokens.text.primary,
              bgcolor: theme.tokens.surface.sunken,
            },
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {([
          { d: 'compact', label: 'Compact', Icon: DensitySmallRounded },
          { d: 'standard', label: 'Standard', Icon: DensityMediumRounded },
          { d: 'comfortable', label: 'Comfortable', Icon: DensityLargeRounded },
        ] as const).map(({ d, label, Icon: I }) => (
          <MenuItem
            key={d}
            selected={value === d}
            onClick={() => { onChange(d); setAnchor(null); }}
            sx={{ fontSize: '0.8125rem', gap: 1 }}
          >
            <I sx={{ fontSize: 16, color: theme.tokens.text.tertiary }} />
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export function SmartTable(props: SmartTableProps) {
  const {
    columns: rawColumns,
    rows: rawRows,
    rowCount,
    hints,
    fillContainer = false,
    variant = 'card',
    initialDensity,
    cardSx,
    title,
    subtitle,
    showToolbar,
    toolbarLeft,
    toolbarActions,
    initialPageSize = 25,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    onFetchPage,
    onAddToDashboard,
    onDownload,
    downloadDisabled,
    enableRowDetail = true,
    onRowClick,
    columnMinWidths,
  } = props;

  // Defensive normalisation — upstream payloads (saved widgets, replayed
  // events) occasionally arrive with missing arrays; we'd rather show an
  // empty grid than crash the page.
  const columns = useMemo(
    () => (Array.isArray(rawColumns) ? rawColumns.filter((c) => typeof c === 'string') : []),
    [rawColumns],
  );
  const rows = useMemo<any[][]>(() => {
    if (!Array.isArray(rawRows)) return [];
    return rawRows.map((r) => (Array.isArray(r) ? r : []));
  }, [rawRows]);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [density, setDensity] = useDensity(initialDensity);
  const [downloading, setDownloading] = useState(false);

  const showToolbarResolved = showToolbar ?? variant === 'card';

  // ── Column meta (memoized) ────────────────────────────────────────────────
  const columnMetas = useColumnMeta(columns, rows, hints);

  // ── Server-side pagination buffer ─────────────────────────────────────────
  // We treat the upstream `rows` array as authoritative for the first
  // ceil(rows.length / pageSize) pages. Anything beyond that triggers
  // `onFetchPage` and the result is cached here keyed by absolute page index.
  const [serverRowsByPage, setServerRowsByPage] = useState<Record<number, any[][]>>({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const totalRowCount = rowCount ?? rows.length;
  const localPagesAvailable = Math.ceil(rows.length / pageSize);

  const handlePaginationChange = useCallback(
    async (model: { page: number; pageSize: number }) => {
      setPage(model.page);
      setPageSize(model.pageSize);
      if (
        onFetchPage &&
        model.page >= localPagesAvailable &&
        !serverRowsByPage[model.page]
      ) {
        try {
          const fetched = await onFetchPage(model.page + 1, model.pageSize);
          setServerRowsByPage((prev) => ({ ...prev, [model.page]: fetched }));
        } catch {
          // swallow — DataGrid will just show the empty page
        }
      }
    },
    [onFetchPage, localPagesAvailable, serverRowsByPage],
  );

  // ── Build DataGrid columns ────────────────────────────────────────────────
  const gridColumns: GridColDef[] = useMemo(() => {
    return columnMetas.map((meta) => {
      const col: GridColDef = {
        field: meta.name,
        headerName: meta.label,
        sortable: true,
        minWidth: columnMinWidths?.[meta.name] ?? 80,
        width: meta.suggestedWidth,
        flex: meta.type === 'long_text' || meta.type === 'text' ? 1 : 0,
        headerAlign: meta.align,
        align: meta.align,
        renderHeader: (params) => <HeaderCell params={params} type={meta.type} />,
        renderCell: (params: GridRenderCellParams) => (
          <SmartCell value={params.value} meta={meta} />
        ),
        valueGetter: (value: any) => value,
        sortComparator: (a, b) => {
          const av = formatForSort(a, meta);
          const bv = formatForSort(b, meta);
          if (typeof av === 'number' && typeof bv === 'number') return av - bv;
          return String(av).localeCompare(String(bv));
        },
      };
      return col;
    });
  }, [columnMetas, columnMinWidths]);

  // ── Row cache (absolute index → row array) ────────────────────────────────
  // Used both by the row-detail drawer and by gridRows. Dense Map; only
  // contains rows we actually have (preview + server-fetched pages).
  const rowsByIdx = useMemo(() => {
    const m = new Map<number, any[]>();
    for (let i = 0; i < rows.length; i++) m.set(i, rows[i]);
    for (const [pStr, pRows] of Object.entries(serverRowsByPage)) {
      const p = Number(pStr);
      const start = p * pageSize;
      for (let i = 0; i < pRows.length; i++) m.set(start + i, pRows[i]);
    }
    return m;
  }, [rows, serverRowsByPage, pageSize]);

  // ── Build DataGrid rows ───────────────────────────────────────────────────
  // Server mode: emit just the current page's rows (DataGrid asks for
  //   `rows.length === pageSize` and uses `rowCount` for the total).
  // Client mode: emit every preview row; DataGrid handles pagination locally.
  const gridRows = useMemo(() => {
    const buildRow = (rowArr: any[], absIdx: number) => {
      const obj: Record<string, any> = { id: absIdx };
      for (let c = 0; c < columns.length; c++) {
        obj[columns[c]] = rowArr[c] ?? null;
      }
      return obj;
    };

    if (onFetchPage) {
      const start = page * pageSize;
      const out: any[] = [];
      for (let i = 0; i < pageSize; i++) {
        const r = rowsByIdx.get(start + i);
        if (r) out.push(buildRow(r, start + i));
      }
      return out;
    }

    return rows.map((row, idx) => buildRow(row, idx));
  }, [rows, columns, rowsByIdx, page, pageSize, onFetchPage]);

  // ── Row detail drawer state ───────────────────────────────────────────────
  const [drawerIdx, setDrawerIdx] = useState<number | null>(null);
  const drawerRowArray: any[] | null =
    drawerIdx !== null ? rowsByIdx.get(drawerIdx) ?? null : null;

  const handleRowClick = useCallback(
    (params: any) => {
      const idx = typeof params.id === 'number' ? params.id : Number(params.id);
      if (onRowClick) {
        const arr = columns.map((c) => params.row?.[c]);
        onRowClick(arr, idx);
        return;
      }
      if (enableRowDetail) setDrawerIdx(idx);
    },
    [onRowClick, enableRowDetail, columns],
  );

  // ── Built-in download handler with loading state ──────────────────────────
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);

  const handleDownloadFormat = useCallback(
    async (format: 'csv' | 'xlsx') => {
      setDownloadAnchor(null);
      if (!onDownload) return;
      setDownloading(true);
      try {
        await onDownload(format);
      } finally {
        setDownloading(false);
      }
    },
    [onDownload],
  );

  // ── Toolbar ───────────────────────────────────────────────────────────────
  const toolbar = showToolbarResolved && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        rowGap: 0.5,
        px: 1.75,
        py: 1,
        borderBottom: `1px solid ${theme.tokens.border.subtle}`,
        bgcolor: theme.tokens.surface.raised,
        flexShrink: 0,
      }}
    >
      <TableChartOutlined sx={{ fontSize: 16, color: theme.tokens.text.tertiary }} />
      {(title || subtitle) ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {title && (
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: theme.tokens.text.primary,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              sx={{
                fontSize: '0.6875rem',
                color: theme.tokens.text.tertiary,
                lineHeight: 1.2,
                mt: 0.25,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography sx={{ ...theme.typography.eyebrow, color: theme.tokens.text.tertiary }}>
          Results
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      {toolbarLeft}
      <Typography sx={{ fontSize: '0.7rem', color: theme.tokens.text.tertiary, mr: 0.5 }}>
        {totalRowCount.toLocaleString()} {totalRowCount === 1 ? 'row' : 'rows'}
      </Typography>
      <DensityToggle value={density} onChange={setDensity} />
      {onDownload && (
        <>
          <Tooltip title={downloading ? 'Preparing download\u2026' : 'Download'} arrow placement="top">
            <span>
              <IconButton
                size="small"
                onClick={(e) => setDownloadAnchor(e.currentTarget)}
                disabled={downloading || downloadDisabled}
                aria-label="Download"
                aria-haspopup="menu"
                sx={{
                  height: 28,
                  width: 'auto',
                  px: 0.5,
                  borderRadius: '6px',
                  color: theme.tokens.text.tertiary,
                  '&:hover': {
                    color: theme.tokens.text.primary,
                    bgcolor: theme.tokens.surface.sunken,
                  },
                }}
              >
                {downloading
                  ? <CircularProgress size={15} thickness={5} sx={{ color: 'inherit' }} />
                  : <CloudDownloadRounded sx={{ fontSize: 16 }} />}
                {!downloading && (
                  <ArrowDropDownRounded sx={{ fontSize: 16, ml: -0.125, opacity: 0.7 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            anchorEl={downloadAnchor}
            open={Boolean(downloadAnchor)}
            onClose={() => setDownloadAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => handleDownloadFormat('xlsx')} sx={{ fontSize: '0.8125rem' }}>
              Download as Excel (.xlsx)
            </MenuItem>
            <MenuItem onClick={() => handleDownloadFormat('csv')} sx={{ fontSize: '0.8125rem' }}>
              Download as CSV
            </MenuItem>
          </Menu>
        </>
      )}
      {onAddToDashboard && (
        <Tooltip title="Pin to dashboard" arrow placement="top">
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddchartRounded sx={{ fontSize: 14 }} />}
            onClick={onAddToDashboard}
            sx={{
              fontSize: '0.75rem',
              py: 0.375,
              px: 1,
              height: 28,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '6px',
              borderColor: theme.tokens.border.default,
              color: theme.tokens.text.secondary,
              minWidth: 0,
              '&:hover': {
                borderColor: theme.tokens.brand.primary,
                color: theme.tokens.brand.primary,
                bgcolor: theme.tokens.brand.primarySoft,
              },
            }}
          >
            Pin
          </Button>
        </Tooltip>
      )}
      {toolbarActions}
    </Box>
  );

  // ── DataGrid styling ──────────────────────────────────────────────────────
  const rowH = rowHeightFor(density);
  const headerH = 36;
  // MUI's TablePagination footer is taller than its `minHeight` because it
  // contains 32px-tall icon buttons + select with their own padding. Reserve
  // 56px in the auto-height calc so the last row never gets clipped.
  const footerH = 56;

  const gridSx = useMemo(
    () => ({
      border: 0,
      borderRadius: 0,
      fontSize: '0.8125rem',
      color: theme.tokens.text.primary,

      '& .MuiDataGrid-columnHeaders': {
        bgcolor: theme.tokens.surface.sunken,
        borderBottom: `1px solid ${theme.tokens.border.subtle}`,
        minHeight: `${headerH}px !important`,
        maxHeight: `${headerH}px !important`,
      },
      '& .MuiDataGrid-columnHeader': {
        '&:focus, &:focus-within': { outline: 'none' },
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 600,
        fontSize: '0.7rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.tokens.text.tertiary,
      },
      '& .MuiDataGrid-columnSeparator': {
        color: alpha(theme.tokens.text.tertiary, 0.3),
        '&:hover': { color: theme.tokens.text.tertiary },
      },

      '& .MuiDataGrid-cell': {
        borderColor: theme.tokens.border.subtle,
        py: 0,
        '&:focus, &:focus-within': { outline: 'none' },
      },

      '& .MuiDataGrid-row': {
        cursor: enableRowDetail || onRowClick ? 'pointer' : 'default',
        '&:hover': {
          bgcolor: isDark
            ? alpha(theme.tokens.brand.primary, 0.08)
            : theme.tokens.brand.primarySofter,
        },
      },

      '& .MuiDataGrid-footerContainer': {
        borderTop: `1px solid ${theme.tokens.border.subtle}`,
        minHeight: footerH,
        bgcolor: theme.tokens.surface.raised,
      },
      '& .MuiTablePagination-root': {
        fontSize: '0.75rem',
        color: theme.tokens.text.secondary,
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: '0.75rem',
      },

      '& .MuiDataGrid-overlay': {
        bgcolor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.8125rem',
        color: theme.tokens.text.tertiary,
        letterSpacing: 0.2,
      },
    }),
    [theme, isDark, headerH, footerH, enableRowDetail, onRowClick],
  );

  // ── Auto-height for non-fillContainer mode ────────────────────────────────
  const autoHeight = useMemo(() => {
    if (fillContainer) return undefined;
    const empty = gridRows.length === 0;
    const visibleRows = Math.min(gridRows.length, pageSize);
    // +8px is a small safety buffer for the column-header / virtual-scroller
    // borders so the last row never gets clipped by a half-pixel rounding.
    const baseH = headerH + footerH + (empty ? 100 : visibleRows * rowH + 8);
    const cap = typeof window !== 'undefined'
      ? window.innerHeight * 0.6
      : 600;
    return Math.min(baseH, cap);
  }, [gridRows.length, pageSize, rowH, headerH, footerH, fillContainer]);

  // ── Final render ──────────────────────────────────────────────────────────
  const cardWrapperSx = variant === 'card'
    ? {
        border: `1px solid ${theme.tokens.border.subtle}`,
        borderRadius: '12px',
        bgcolor: theme.tokens.surface.raised,
        boxShadow: theme.tokens.shadow.xs,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column' as const,
        ...cardSx,
      }
    : {
        display: 'flex',
        flexDirection: 'column' as const,
        ...cardSx,
      };

  const containerSx = fillContainer
    ? { ...cardWrapperSx, height: '100%', width: '100%' }
    : { ...cardWrapperSx, width: '100%' };

  return (
    <Box sx={containerSx}>
      {toolbar}
      <Box sx={{ flex: fillContainer ? 1 : undefined, minHeight: 0, height: fillContainer ? undefined : autoHeight }}>
        <DataGrid
          rows={gridRows}
          columns={gridColumns}
          rowHeight={rowH}
          columnHeaderHeight={headerH}
          pageSizeOptions={pageSizeOptions}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationChange}
          paginationMode={onFetchPage ? 'server' : 'client'}
          rowCount={onFetchPage ? totalRowCount : undefined}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          sx={gridSx}
        />
      </Box>
      <RowDetailDrawer
        open={drawerIdx !== null}
        onClose={() => setDrawerIdx(null)}
        row={drawerRowArray}
        rowIndex={drawerIdx ?? 0}
        totalRows={totalRowCount}
        columns={columnMetas}
        title={title}
        onPrev={() => setDrawerIdx((i) => (i !== null && i > 0 ? i - 1 : i))}
        onNext={() =>
          setDrawerIdx((i) =>
            i !== null && i < totalRowCount - 1 ? i + 1 : i,
          )
        }
      />
    </Box>
  );
}

// Suppress unused warning — `ViewCompactRounded` reserved for future toggle.
void ViewCompactRounded;
